// Classes of micro-2d-app's shadow-DOM UI overlays that must be excluded from
// dealer comparison snapshots. We want a clean product image of the cars only —
// no nav arrows, fullscreen button, page counter, or compare-mode toggle bar.
const SNAPSHOT_EXCLUDED_CLASSES = new Set([
  'carousel__buttons', // left/right navigation arrows
  'carousel__selected-snap-wrapper', // "1/3" page counter
  'carousel__resize-button-wrapper', // fullscreen toggle
  'compare-button-wrapper', // swap / slider / compare-mode buttons row
]);

/**
 * Captures an HTML element as a PNG blob using html-to-image.
 * PNG is chosen over JPEG so the snapshot preserves the alpha channel from the
 * source car renders (transparent backgrounds), letting dealers use the image
 * as a clean product cutout. Uses dynamic import for code splitting.
 */
export async function captureElementSnapshot(el: HTMLElement): Promise<Blob> {
  if (!el) {
    throw new Error('Element is required for snapshot capture');
  }

  // Pre-flight guard: html-to-image sizes the canvas from clientWidth/clientHeight.
  // A 0x0 canvas causes `canvas.toBlob(cb)` to call back with null — an opaque
  // failure. Fail fast with element identity for diagnosis.
  if (el.clientWidth === 0 || el.clientHeight === 0) {
    throw new Error(`Cannot capture element with zero dimensions: ${describeElement(el)}`);
  }

  // AVS-4167: Inline every <img> inside the capture target as a data URL
  // BEFORE handing off to html-to-image. Two prior approaches failed:
  //   1. fetchRequestInit:{cache:'reload'} — controls HTTP cache only, not
  //      WebKit's per-resource image memory cache.
  //   2. cacheBust:true — the CDN serving the car renders (mediaservice.audi.com)
  //      uses a signed URL and rejects any extra query string with HTTP 400,
  //      so a cache-busted URL never resolves at all.
  // The actual pipeline is implemented in `loadAsCorsDataUrl`: fetch() the bytes
  // in CORS mode (bypasses WebKit's per-resource image cache), then decode the
  // resulting blob via a same-origin blob: URL into a fresh Image, draw onto a
  // canvas, and re-encode with `canvas.toDataURL('image/png')`. Re-encoding to
  // PNG preserves the source alpha channel (JPEG would collapse to opaque black)
  // and normalises WebP into a format WebKit's foreignObject renderer accepts.
  // html-to-image never has to fetch — it sees a data URL and embeds directly.
  const restoreImages = await inlineImagesAsDataUrls(el);

  try {
    const { toBlob } = await import('html-to-image');

    // WebKit workaround: call toBlob TWICE. html-to-image renders the cloned
    // DOM into an <svg><foreignObject> container, serialises that SVG to a
    // data URL, loads it into an Image, then drawImage()s onto a canvas.
    // Safari's outer-Image onload fires before inner data-URL <img>s finish
    // rasterising inside the foreignObject, so the first canvas snapshot is
    // blank. The second call has warm decoded state and produces the real
    // image. Chromium completes on the first call but tolerates the second.
    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      blob = await toBlob(el, {
        type: 'image/png',
        // Skip shadow-DOM UI overlays — dealers want a clean product image.
        filter: snapshotNodeFilter,
        // Defense-in-depth for any remote resource we couldn't inline
        // (CSS background-image, SVG xlink:href).
        fetchRequestInit: { cache: 'reload' },
      });
    }

    if (!blob) {
      throw new Error('Failed to capture element snapshot');
    }

    return blob;
  } finally {
    restoreImages();
  }
}

function snapshotNodeFilter(node: HTMLElement): boolean {
  if (!node.classList) return true;
  for (const cls of SNAPSHOT_EXCLUDED_CLASSES) {
    if (node.classList.contains(cls)) return false;
  }
  return true;
}

function describeElement(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes = el.className ? `.${el.className.trim().split(/\s+/).join('.')}` : '';
  return `${tag}${id}${classes}`;
}

function collectImages(root: HTMLElement): HTMLImageElement[] {
  const found: HTMLImageElement[] = [];
  const visit = (node: ParentNode) => {
    node.querySelectorAll('img').forEach((img) => found.push(img));
    node.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) visit(el.shadowRoot);
    });
  };
  visit(root);
  if (root.shadowRoot) visit(root.shadowRoot);
  return found;
}

/**
 * Pre-converts every <img> reachable from `root` (including shadow DOM) into a
 * data URL stored on its own src. Returns a restore function that puts the
 * original src values back. Individual conversion failures are swallowed so a
 * single broken image doesn't abort the whole snapshot — that image will simply
 * render blank in the output (same as html-to-image's own behaviour).
 */
async function inlineImagesAsDataUrls(root: HTMLElement): Promise<() => void> {
  const images = collectImages(root);
  const originals: Array<[HTMLImageElement, string]> = [];

  await Promise.all(
    images.map(async (img) => {
      const originalSrc = img.src;
      if (!originalSrc || originalSrc.startsWith('data:')) return;

      try {
        const dataUrl = await loadAsCorsDataUrl(originalSrc);
        originals.push([img, originalSrc]);
        img.src = dataUrl;
      } catch {
        // Conversion failed (network error, tainted canvas, etc.). Leave the
        // original src in place — html-to-image will attempt its own fetch
        // path and at worst this single image renders blank in the snapshot.
      }
    })
  );

  return () => {
    originals.forEach(([img, src]) => {
      img.src = src;
    });
  };
}

async function loadAsCorsDataUrl(url: string): Promise<string> {
  // Two stages — both required for WebKit:
  //
  // Stage A: fetch() the bytes ourselves with mode:'cors' + cache:'reload'.
  // Bypasses the HTMLImageElement code path entirely. `<img>` in WebKit shares
  // a per-resource memory cache that retains the original no-CORS page-render
  // load even when crossOrigin='anonymous' is set on a probe Image. fetch()
  // uses a separate response cache and reliably issues a fresh CORS-mode
  // request, returning the raw bytes.
  //
  // Stage B: decode the blob and re-encode as PNG via a same-origin blob URL.
  // We can't just FileReader-encode the blob bytes into a data:image/webp;...
  // URL: WebKit's SVG-foreignObject renderer (used internally by html-to-image)
  // does NOT decode WebP inside <img> children of foreignObject, so the final
  // snapshot comes back blank even though the data URL is well-formed.
  // Decoding via canvas and re-encoding with canvas.toDataURL('image/png')
  // gives us a plain PNG data URL that WebKit's renderer handles correctly,
  // and the canvas is untainted (blob: URLs are same-origin).
  const blob = await fetchAsBlob(url);
  return await reencodeBlobAsPngDataUrl(blob);
}

async function fetchAsBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: 'cors', cache: 'reload' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

function reencodeBlobAsPngDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    // Scoped inside the executor so the matching revoke is paired only when
    // createObjectURL actually succeeded — avoids revokeObjectURL(undefined)
    // if URL is unavailable in some exotic host context.
    const objectUrl = URL.createObjectURL(blob);
    const cleanup = () => URL.revokeObjectURL(objectUrl);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          reject(new Error('2D canvas context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        cleanup();
        resolve(dataUrl);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };
    img.onerror = () => {
      cleanup();
      reject(new Error('Failed to decode image blob'));
    };
    img.src = objectUrl;
  });
}
